import { Alert, Box, Paper, Stack, Typography, useTheme } from "@mui/material";
import { useIsMobile } from "../../hook/useIsMobile";
import MultipleSelect from "../select/multipleSelect.component";
import TimeRangeButtons from "../buttons/timeRangeButtons.component";
import { getChartErrorMessage, getMeasuresLabel, parseMeasureId, projectMeasureData } from "../chart/utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import SensorChart from "../chart/sensorChart.component";
import { useSensorDataFetch } from "../../hook/useSensorDataFetch";
import { useTimeRange } from "../../hook/useTimeRange";
import LoadingChartState from "../chart/loadingChartState.component";
import { selectSensorDataState } from "../../store/selectors/sensorDataSelectors";

const SensorChartPanel = ({ sensors, accessToken, title, subtitle, scope, filtered }) => {
  const isMobile = useIsMobile();
  const theme = useTheme();

  const [selectedMeasures, setSelectedMeasures] = useState([]);

  const { data: readings, isLoading: loadingState, error } = useSelector(
    (state) => selectSensorDataState(state, scope),
  );

  const {
    selectedButton,
    timeRange,
    isTimeRangeReady,
    handleButtonClick,
    handleDateRangeChange,
  } = useTimeRange();

  const chartData = useMemo(() => Object.fromEntries(
    selectedMeasures.map((key) => {
      const { sensorId, measure } = parseMeasureId(key);
      return [key, projectMeasureData(readings[sensorId] || [], measure)];
    }),
  ), [readings, selectedMeasures]);

  const measureList = useMemo(() => {
    return getMeasuresLabel(sensors);
  }, [sensors]);

  useEffect(() => {
    const validValues = new Set(measureList.map((item) => item.value));
    setSelectedMeasures((previous) => previous.every((value) => validValues.has(value))
      ? previous : previous.filter((value) => validValues.has(value)));
  }, [measureList]);

  const handleMeasuresChange = useCallback((measures) => {
    setSelectedMeasures(measures);
  }, []);

  useSensorDataFetch({
    sensorIds: selectedMeasures.map((key) => parseMeasureId(key).sensorId),
    timeRange,
    accessToken,
    selectedButton,
    scope,
    filtered,
    enabled: selectedMeasures.length > 0 && isTimeRangeReady,
  });

  return (
    <Paper
      sx={{
        p: 3,
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
      elevation={0}
    >
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        justifyContent="space-between"
        mb={1}
        gap={2}
      >
        <Box display="flex" flexDirection="row" gap={2}>
          <Stack direction="column">
            <Typography fontWeight="bold" fontSize="14px">
              {title}
            </Typography>
            <Typography
              fontSize="12px"
              color={theme.palette.custom.infoTextColor}
            >
              {subtitle}
            </Typography>
          </Stack>
          <MultipleSelect
            isMobile={isMobile}
            measureList={measureList}
            onChange={handleMeasuresChange}
            isCurrent={true}
            value={selectedMeasures}
          />
        </Box>
        {selectedMeasures.length > 0 ? (
          <TimeRangeButtons
            selectedButton={selectedButton}
            handleButtonClick={handleButtonClick}
            onDateRangeChange={handleDateRangeChange}
          />
        ) : undefined}
      </Box>
      {error && <Alert severity="error">{getChartErrorMessage(error)}</Alert>}
      {selectedMeasures.length === 0 ? (
        <Box
          width="100%"
          height="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Typography variant="body1" color="text.secondary" textAlign="center">
            Seleziona da una a tre misure per visualizzare i dati nel grafico
          </Typography>
        </Box>
      ) : (
        <Box position="relative" width="100%" height="100%">
          {loadingState && <LoadingChartState />}
          <SensorChart
            chartData={chartData}
            selectedButton={selectedButton}
            selectedMeasures={selectedMeasures}
            sensors={sensors}
            isLoading={loadingState}
            error={error}
          />
        </Box>
      )}
    </Paper>
  );
};

export default SensorChartPanel;
